import React, {useEffect, useState} from 'react';
import {Box, Card, Collapse, Stack, Tooltip, Typography} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import RepeatIcon from '@mui/icons-material/Repeat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import SectionTaskCard from './SectionTaskCard';
import {DeleteWholeSeriesDialog, EditSeriesDialog} from './SeriesDialogs';
import {byDate, nextOccurrence, seriesName} from '../../utilities/groupSeries';
import {BoardRole, canEditBoard} from '../../types/task';
import type {TaskFocus} from './Board';

type SeriesCardProps = {
    // Every task in the series that sits in this section (two or more), in any order.
    tasks: any[];
    viewerRole?: BoardRole;
    // Set when a search result inside this series was picked; the card opens to show it.
    focus?: TaskFocus;
};

// One card standing in for a whole recurring series on the board view. Clicking it
// expands the series into its individual task cards.
function SeriesCard({tasks, viewerRole, focus}: SeriesCardProps) {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const canEdit = canEditBoard(viewerRole);

    useEffect(() => {
        if (focus) setExpanded(true);
    }, [focus?.nonce]);

    const ordered = [...tasks].sort(byDate);
    const completedCount = ordered.filter((t) => t.isCompleted).length;
    const now = new Date().toISOString();
    // The soonest occurrence that hasn't happened yet; the edit dialog treats it as the
    // pivot, so "this and following" means "from the next one on".
    const next = ordered.find((t) => t.date && t.date >= now);
    // What the series is called, and its note, are the next occurrence's: an earlier one may still carry a name it was renamed from.
    const current = nextOccurrence(ordered, now);
    const name = seriesName(ordered, now);

    const summary = [
        `${ordered.length} occurrences`,
        `${completedCount} completed`,
        next ? `next ${dayjs(next.date).format('ddd, MMM D · h:mm A')}` : null,
    ].filter(Boolean).join(' · ');

    return (
        <>
            <Card variant="outlined" sx={{
                borderRadius: 2,
                borderLeft: '4px solid',
                borderLeftColor: completedCount === ordered.length ? 'success.main' : 'primary.main',
                transition: 'box-shadow 0.15s',
                '&:hover': {boxShadow: 2},
            }}>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <ButtonBase
                        onClick={() => setExpanded((open) => !open)}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} recurring task ${name}`}
                        sx={{flexGrow: 1, display: 'flex', justifyContent: 'flex-start', textAlign: 'left', p: 2, gap: 1.5}}
                    >
                        <RepeatIcon color="action"/>
                        <Box sx={{flexGrow: 1, minWidth: 0}}>
                            <Typography variant="body1" noWrap>{name}</Typography>
                            <Typography variant="caption" color="text.secondary">{summary}</Typography>
                            {current?.description?.trim() && (
                                <Typography variant="body2" color="text.secondary" noWrap>{current.description.trim()}</Typography>
                            )}
                        </Box>
                        <ExpandMoreIcon sx={{transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}}/>
                    </ButtonBase>
                    {canEdit && (
                        <>
                            <Tooltip title="Edit the whole series">
                                <IconButton aria-label="Edit recurring series" onClick={() => setEditing(true)}>
                                    <EditIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete the whole series">
                                <IconButton
                                    aria-label="Delete recurring series"
                                    onClick={() => setDeleting(true)}
                                    sx={{mr: 1, color: 'text.secondary', '&:hover': {color: 'error.main'}}}
                                >
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>

                <Collapse in={expanded} unmountOnExit>
                    <Stack spacing={1} sx={{p: 1, pt: 0}}>
                        {ordered.map((task) => (
                            <SectionTaskCard key={task.id} id={task.id} boardId={task.boardId} viewerRole={viewerRole} fluid/>
                        ))}
                    </Stack>
                </Collapse>
            </Card>

            {editing && (
                <EditSeriesDialog task={current} onClose={() => setEditing(false)}/>
            )}
            {deleting && (
                <DeleteWholeSeriesDialog task={current} name={name} onClose={() => setDeleting(false)}/>
            )}
        </>
    );
}

export default SeriesCard;
